import { useState } from "react";
import axios from "axios";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  phone: "",
  email: "",
  location: "",
  property_type: "Residential",
  system_size: "",
  message: "",
};

const propertyOptions = ["Residential", "Commercial", "Industrial"];
const sizeOptions = [
  "Not sure — please advise",
  "Below 5 kW",
  "5 – 10 kW",
  "10 – 25 kW",
  "25 – 100 kW",
  "Above 100 kW",
];

export default function EnquiryForm() {
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.name || !data.phone || !data.location) {
      toast.error("Please fill your name, phone & location.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...data };
      if (!payload.email) delete payload.email;
      await axios.post(`${API}/enquiries`, payload);
      setDone(true);
      setData(initial);
      toast.success("Thank you! Our team will reach out within 24 hours.");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail || "Could not submit — please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="enquiry"
      data-testid="enquiry-section"
      className="section-pad relative overflow-hidden"
    >
      <div className="absolute inset-0 -z-10 opacity-40">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 h-[600px] w-[600px] sun-ring" />
      </div>

      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-[#FFC857] mb-6">
              — Enquiry
            </div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-light leading-[1.1]">
              Tell us about your{" "}
              <span className="italic text-[#FFC857]">rooftop.</span>
            </h2>
            <p className="mt-6 text-[#A0ABC0] text-base md:text-lg font-light leading-relaxed">
              Share a few details and we'll send back a free site assessment,
              a custom design and an honest savings projection — usually
              within 24 hours.
            </p>

            <div className="mt-10 space-y-4 text-sm text-[#A0ABC0]">
              <div className="flex items-center gap-3">
                <span className="h-1 w-6 bg-[#FFC857]" /> Free site survey
              </div>
              <div className="flex items-center gap-3">
                <span className="h-1 w-6 bg-[#FFC857]" /> 7-day project
                handover
              </div>
              <div className="flex items-center gap-3">
                <span className="h-1 w-6 bg-[#FFC857]" /> Tier-1 components
                only
              </div>
              <div className="flex items-center gap-3">
                <span className="h-1 w-6 bg-[#FFC857]" /> Lifetime support
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="enquiry-form"
              className="glass rounded-sm p-8 md:p-10"
            >
              {done && (
                <div
                  data-testid="enquiry-success"
                  className="mb-6 flex items-start gap-3 rounded-sm border border-[#FFC857]/30 bg-[#FFC857]/10 p-4"
                >
                  <CheckCircle2 className="h-5 w-5 text-[#FFC857] mt-0.5" />
                  <div className="text-sm text-white font-light">
                    Enquiry received. Our team will call you on the number you
                    provided within 24 hours.
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Full Name *" testId="form-name">
                  <input
                    data-testid="input-name"
                    value={data.name}
                    onChange={upd("name")}
                    required
                    placeholder="e.g. Ramesh Kumar"
                    className={inputCls}
                  />
                </Field>

                <Field label="Phone *" testId="form-phone">
                  <input
                    data-testid="input-phone"
                    value={data.phone}
                    onChange={upd("phone")}
                    required
                    type="tel"
                    placeholder="9740888373"
                    className={inputCls}
                  />
                </Field>

                <Field label="Email" testId="form-email">
                  <input
                    data-testid="input-email"
                    value={data.email}
                    onChange={upd("email")}
                    type="email"
                    placeholder="you@example.com"
                    className={inputCls}
                  />
                </Field>

                <Field label="Location *" testId="form-location">
                  <input
                    data-testid="input-location"
                    value={data.location}
                    onChange={upd("location")}
                    required
                    placeholder="City / Area in Karnataka"
                    className={inputCls}
                  />
                </Field>

                <Field label="Property Type" testId="form-property">
                  <select
                    data-testid="input-property"
                    value={data.property_type}
                    onChange={upd("property_type")}
                    className={inputCls}
                  >
                    {propertyOptions.map((p) => (
                      <option key={p} value={p} className="bg-[#0A1128]">
                        {p}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="System Size" testId="form-size">
                  <select
                    data-testid="input-size"
                    value={data.system_size}
                    onChange={upd("system_size")}
                    className={inputCls}
                  >
                    <option value="" className="bg-[#0A1128]">
                      Select a size
                    </option>
                    {sizeOptions.map((s) => (
                      <option key={s} value={s} className="bg-[#0A1128]">
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field
                label="Message"
                testId="form-message"
                className="mt-5"
              >
                <textarea
                  data-testid="input-message"
                  value={data.message}
                  onChange={upd("message")}
                  rows={4}
                  placeholder="Tell us about your roof type, electricity bill, timeline…"
                  className={`${inputCls} resize-none`}
                />
              </Field>

              <button
                data-testid="enquiry-submit"
                type="submit"
                disabled={submitting}
                className="btn-gold mt-7 w-full md:w-auto justify-center disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
                  </>
                ) : (
                  <>
                    Request Free Quote <Send className="h-4 w-4" />
                  </>
                )}
              </button>

              <p className="mt-5 text-xs text-[#A0ABC0]/80 font-light">
                By submitting, you agree to be contacted by Jashvi Energy
                regarding your enquiry. We never share your details.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

const inputCls =
  "w-full bg-[#050B14]/60 border border-white/10 focus:border-[#FFC857]/60 focus:outline-none rounded-sm px-4 py-3.5 text-white placeholder:text-[#A0ABC0]/60 text-sm font-light transition-colors";

function Field({ label, children, className = "", testId }) {
  return (
    <label data-testid={testId} className={`block ${className}`}>
      <span className="block text-[11px] uppercase tracking-[0.22em] text-[#A0ABC0] mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
